import classNames from 'classnames';
import get from 'lodash/get';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import { ChildrenPropType } from 'src/utils/types';

type Props = {
  children: React.ReactElement;
  content: (close: () => void) => ChildrenPropType;

  header?: ChildrenPropType;
  placement?: 'bottom' | 'top' | 'top-end' | 'bottom-end';
  className?: string;
  customClose?: boolean; // custom handle to not close when you click on an inner datepicker
};

export type UiPopoverProps = Props;

/**
 * popover component for showing any content
 */
export const UiPopover: React.FC<Props> = memo(
  ({ children, customClose, content, header, placement = 'bottom', className }: Props) => {
    const [show, setShow] = useState(false);

    const close = useCallback(() => setShow(false), []);

    const ref = useRef<HTMLDivElement>(null);

    const onOuterClick = useCallback(
      ({ target }: Event) => {
        const closeCondition = ref.current && !ref.current.contains(target as Node);

        if (customClose) {
          const hasElement: string[] = get(target, 'parentNode.parentNode.className', []);
          const clickOnDatePicker: boolean = hasElement ? hasElement.includes('flatpickr') : false;

          if (!clickOnDatePicker && closeCondition) {
            close();
          }
        } else if (closeCondition) {
          close();
        }
      },
      [close, customClose]
    );

    useEffect(() => {
      if (customClose) {
        if (show) {
          setTimeout(() => {
            window.addEventListener('click', onOuterClick);
          }, 0);
        } else {
          window.removeEventListener('click', onOuterClick);
        }
      }

      return () => {
        window.removeEventListener('click', onOuterClick);
      };
    }, [show, onOuterClick, customClose]);

    return (
      <OverlayTrigger
        trigger="click"
        rootClose={!customClose}
        placement={placement}
        show={show}
        onToggle={setShow}
        overlay={
          <Popover className={classNames('r-popover not-custom-branding-popover', placement, className)} id="popoverid">
            <div ref={ref}>
              {header && <h3 className="popover-title">{header}</h3>}
              <div className="popover-content">{content(close)}</div>
            </div>
          </Popover>
        }
      >
        {children}
      </OverlayTrigger>
    );
  }
);
